// ตาราง what-if + ของจริง ของหน้าความจุบัญชี — DataTable ไม่เปิด sort/pagination (แถวมี input, sort ด้วย item[key] จะสลับแถวตอนพิมพ์)
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCircleCheck, faGaugeHigh, faTriangleExclamation, faBan } from '@fortawesome/free-solid-svg-icons';
// พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง): dayjs ใช้เฉพาะใน PeakCell
// import dayjs from 'dayjs';
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';
import DataTable, { Column } from '../../../components/shared/DataTable';
// import { StatusBadge } from '../../../components/ui'; // พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง)
import type { StatusDef } from '../../../components/ui';
import { fmtNum } from '../../../utils/formatMoney';
import { fmtWindow, getBankStyle } from '../../../utils/bankLimit';
import LimitInput from './LimitInput';
import { LIMIT_RANGES } from './useWhatIfRows';
import type { LimitPatch } from './useWhatIfRows';
import { fmtCap } from './capacityMath';
import type { Capacity, HourPeak, WindowPeak, WindowStatus } from './capacityMath';
import type { BankDayActual, WhatIfRow } from './types';

// dayjs.extend(utc);
// dayjs.extend(timezone);
// const TZ = 'Asia/Bangkok'; // พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง)

/** แถวที่คำนวณแล้ว (หน้าหลักเป็นคนคำนวณใน useMemo — ตารางแค่แสดง) */
export interface ComputedRow {
  row: WhatIfRow;
  cap: Capacity;
  actual?: BankDayActual;
  peak: WindowPeak | null;
  usagePct: number | null;
  status: WindowStatus | null;
  hourPeak: HourPeak | null;
}

export const WINDOW_STATUS_MAP: Record<WindowStatus, StatusDef> = {
  free:     { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: faCircleCheck,         label: 'ว่าง' },
  moderate: { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-300',   icon: faGaugeHigh,           label: 'ปานกลาง' },
  near:     { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', icon: faTriangleExclamation, label: 'ใกล้เต็ม' },
  hit:      { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-300',     icon: faBan,                 label: 'ชน' },
};

/* พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง): progress bar ของ PeakCell
const BAR_COLOR: Record<WindowStatus, string> = {
  free: 'bg-green-500', moderate: 'bg-blue-500', near: 'bg-amber-500', hit: 'bg-red-500',
};
*/

const DASH = <span className="text-gray-300 dark:text-slate-600">—</span>;
const NUM_CELL = 'text-right tabular-nums';
const SUB_CLASS = 'text-[10px] text-gray-400 dark:text-slate-500 mt-0.5';
const FOOT_CELL = 'px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-slate-200';
const NAME_INPUT_CLASS =
  'w-40 px-2 py-1 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600';

interface CapacityTableProps {
  rows: ComputedRow[];
  totals: Capacity;
  /** Σ order ของบัญชีในตาราง (ไม่รวมบัญชีที่ปิด/ถอนแล้วแต่ยังมี order) */
  totalOrders: number;
  reportSupported: boolean;
  windowSupported: boolean;
  onChange: (id: string, patch: LimitPatch) => void;
  onRemove: (id: string) => void;
}

const BankCell: React.FC<{ row: WhatIfRow; onChange: CapacityTableProps['onChange'] }> = ({ row, onChange }) => {
  if (row.kind === 'hypo') {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex-shrink-0">สมมุติ</span>
        <input
          type="text"
          value={row.name}
          onChange={(e) => onChange(row.id, { name: e.target.value })}
          className={NAME_INPUT_CLASS}
          aria-label="ชื่อบัญชีสมมุติ"
        />
      </div>
    );
  }
  const style = getBankStyle(row.bankCode || '');
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-9 h-9 rounded-lg ${style.bg} ${style.text} flex items-center justify-center font-bold text-[10px] shadow-sm flex-shrink-0`}>
        {row.bankCode}
      </div>
      <div className="min-w-0">
        <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{row.name}</p>
        <p className={SUB_CLASS}>{row.accountNumber}</p>
      </div>
    </div>
  );
};

/* พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง) — คอลัมน์ x/N + % + badge + เวลาพีค (เปิดกลับ: เอาคอมเมนต์ออกทั้งก้อนนี้ + import dayjs/StatusBadge + BAR_COLOR + บรรทัด column + colSpan footer)
const PeakCell: React.FC<{ c: ComputedRow }> = ({ c }) => {
  const { row, actual, peak, usagePct, status } = c;
  if (!actual || !peak) return DASH;
  if (row.n <= 0) {
    return (
      <div className={NUM_CELL}>
        <span className="font-medium text-gray-700 dark:text-slate-200">{fmtNum(peak.count)}</span>
        <p className={SUB_CLASS}>ปิดหน้าต่าง</p>
      </div>
    );
  }
  const pct = usagePct ?? 0;
  return (
    <div className="min-w-[170px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold tabular-nums text-gray-700 dark:text-slate-200">
          {fmtNum(peak.count)}/{fmtNum(row.n)} <span className="text-gray-400 dark:text-slate-500 font-normal">({Math.round(pct)}%)</span>
        </span>
        {status && <StatusBadge status={status} statusMap={WINDOW_STATUS_MAP} />}
      </div>
      <div className="w-full bg-gray-100 dark:bg-slate-600 rounded-full h-1.5 mt-1">
        <div className={`h-1.5 rounded-full ${status ? BAR_COLOR[status] : 'bg-gray-300'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {peak.endMs !== null && (
        <p className={SUB_CLASS}>
          พีคช่วงถึง {dayjs(peak.endMs).tz(TZ).format('HH:mm:ss')}
          {actual.truncated ? ' · คำนวณจาก 20,000 ใบแรก' : ''}
        </p>
      )}
    </div>
  );
};
*/

const CapacityTable: React.FC<CapacityTableProps> = ({
  rows, totals, totalOrders, reportSupported, windowSupported, onChange, onRemove,
}) => {
  const windowTitle = windowSupported ? undefined : 'tenant นี้ยังไม่เปิดเพดานช่วงเวลา';

  const limitColumns: Column<ComputedRow>[] = [
    {
      key: 'n',
      header: 'N รายการ',
      className: NUM_CELL,
      render: ({ row }) => (
        <div>
          <LimitInput value={row.n} min={LIMIT_RANGES.n.min} max={LIMIT_RANGES.n.max} disabled={!windowSupported} title={windowTitle} ariaLabel={`N ของ ${row.name}`} onCommit={(v) => onChange(row.id, { n: v })} />
          <p className={SUB_CLASS}>{row.n > 0 ? 'ต่อหน้าต่าง' : 'ปิด'}</p>
        </div>
      ),
    },
    {
      key: 's',
      header: 'ต่อ S วินาที',
      className: NUM_CELL,
      render: ({ row }) => (
        <div>
          <LimitInput value={row.s} min={LIMIT_RANGES.s.min} max={LIMIT_RANGES.s.max} disabled={!windowSupported} title={windowTitle} ariaLabel={`S ของ ${row.name}`} onCommit={(v) => onChange(row.id, { s: v })} />
          <p className={SUB_CLASS}>= {fmtWindow(row.s)}</p>
        </div>
      ),
    },
    {
      key: 'd',
      header: 'เพดาน/วัน',
      className: NUM_CELL,
      render: ({ row }) => (
        <div>
          <LimitInput value={row.d} min={LIMIT_RANGES.d.min} max={LIMIT_RANGES.d.max} ariaLabel={`เพดานรายวันของ ${row.name}`} onCommit={(v) => onChange(row.id, { d: v })} />
          <p className={SUB_CLASS}>{row.d > 0 ? 'รายการ/วัน' : 'ไม่จำกัด'}</p>
        </div>
      ),
    },
  ];

  const capacityColumns: Column<ComputedRow>[] = [
    { key: 'perMin',  header: 'รับได้/นาที', className: NUM_CELL, render: ({ cap }) => <span className="font-semibold text-gray-700 dark:text-slate-200">{fmtCap(cap.perMin)}</span> },
    { key: 'perHour', header: 'รับได้/ชม.',  className: NUM_CELL, render: ({ cap }) => <span className="font-semibold text-gray-700 dark:text-slate-200">{fmtCap(cap.perHour)}</span> },
    { key: 'perDay',  header: 'รับได้/วัน',  className: NUM_CELL, render: ({ cap }) => <span className="font-semibold text-gray-700 dark:text-slate-200">{fmtCap(cap.perDay)}</span> },
  ];

  const actualColumns: Column<ComputedRow>[] = reportSupported ? [
    {
      key: 'orders',
      header: 'ออเดอร์จริง',
      className: NUM_CELL,
      render: ({ row, actual }) => {
        if (row.kind === 'hypo') return DASH;
        const a = actual;
        return (
          <div>
            <span className="font-semibold text-gray-700 dark:text-slate-200">{fmtNum(a?.total || 0)}</span>
            <p className={SUB_CLASS}>สำเร็จ {fmtNum(a?.paid || 0)} · หมดอายุ/ยกเลิก {fmtNum((a?.expired || 0) + (a?.cancelled || 0))}</p>
          </div>
        );
      },
    },
    // { key: 'peak', header: 'พีคในหน้าต่าง S', render: (c) => <PeakCell c={c} /> }, // พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง)
    {
      key: 'peakHour',
      header: 'ชั่วโมงพีค',
      render: ({ hourPeak }) => hourPeak ? (
        <span className="text-xs tabular-nums text-gray-700 dark:text-slate-200">
          {String(hourPeak.hour).padStart(2, '0')}:00 <span className="text-gray-400 dark:text-slate-500">({fmtNum(hourPeak.count)})</span>
        </span>
      ) : DASH,
    },
  ] : [];

  const columns: Column<ComputedRow>[] = [
    { key: 'bank', header: 'บัญชี', render: ({ row }) => <BankCell row={row} onChange={onChange} /> },
    ...limitColumns,
    ...capacityColumns,
    ...actualColumns,
    {
      key: 'actions',
      header: '',
      render: ({ row }) => row.kind === 'hypo' ? (
        <button
          type="button"
          onClick={() => onRemove(row.id)}
          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
          title="ลบบัญชีสมมุติ"
        >
          <FontAwesomeIcon icon={faTrash} className="text-xs" />
        </button>
      ) : null,
    },
  ];

  const realCount = rows.filter((c) => c.row.kind === 'real').length;
  const hypoCount = rows.length - realCount;

  const footer = (
    <tr>
      <td className={FOOT_CELL} colSpan={4}>
        รวม {rows.length} บัญชี
        <span className="font-normal text-gray-400 dark:text-slate-500"> (จริง {realCount}{hypoCount > 0 ? ` · สมมุติ ${hypoCount}` : ''})</span>
      </td>
      <td className={`${FOOT_CELL} ${NUM_CELL}`}>{fmtCap(totals.perMin)}</td>
      <td className={`${FOOT_CELL} ${NUM_CELL}`}>{fmtCap(totals.perHour)}</td>
      <td className={`${FOOT_CELL} ${NUM_CELL}`}>{fmtCap(totals.perDay)}</td>
      {reportSupported && (
        <>
          <td className={`${FOOT_CELL} ${NUM_CELL}`}>{fmtNum(totalOrders)}</td>
          {/* ชั่วโมงพีค (เดิม colSpan={2} รวมคอลัมน์พีคในหน้าต่าง S) */}
          <td className={FOOT_CELL} />
        </>
      )}
      <td className={FOOT_CELL} />
    </tr>
  );

  return (
    <DataTable<ComputedRow>
      data={rows}
      columns={columns}
      keyExtractor={(c) => c.row.id}
      compact
      hoverable={false}
      emptyMessage="ไม่มีบัญชีฝากที่เปิดใช้งาน — เพิ่มบัญชีสมมุติเพื่อลองคำนวณได้"
      footer={footer}
    />
  );
};

export default CapacityTable;
